import { useEffect, useRef } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from "lucide-react";

const TOAST_DURATION_MS = 5_000;
const MAX_VISIBLE = 5;

const TOAST_STYLES = {
  info: { icon: Info, border: "border-blue-500/30", text: "text-blue-400" },
  success: { icon: CheckCircle, border: "border-emerald-500/30", text: "text-emerald-400" },
  warning: { icon: AlertTriangle, border: "border-amber-500/30", text: "text-amber-400" },
  error: { icon: AlertCircle, border: "border-red-500/30", text: "text-red-400" },
};

export function NotificationToast() {
  const notifications = useAppStore((s) => s.notifications);
  const dismissNotification = useAppStore((s) => s.dismissNotification);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const visible = notifications.slice(-MAX_VISIBLE);

  useEffect(() => {
    for (const n of visible) {
      if (!timersRef.current.has(n.id)) {
        const timer = setTimeout(() => {
          dismissNotification(n.id);
          timersRef.current.delete(n.id);
        }, TOAST_DURATION_MS);
        timersRef.current.set(n.id, timer);
      }
    }

    return () => {
      for (const timer of timersRef.current.values()) {
        clearTimeout(timer);
      }
    };
  }, [visible, dismissNotification]);

  if (visible.length === 0) return null;

  return (
    <div className="fixed right-4 top-16 z-50 flex flex-col gap-2 w-80" aria-live="polite">
      {visible.map((n) => {
        const style = TOAST_STYLES[n.type];
        const Icon = style.icon;
        return (
          <div
            key={n.id}
            className={`flex items-start gap-3 rounded-lg border ${style.border} bg-slate-800 p-3 shadow-lg animate-toast-enter`}
          >
            <Icon size={16} className={`mt-0.5 shrink-0 ${style.text}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200">{n.title}</p>
              {n.message && (
                <p className="mt-0.5 text-xs text-slate-400 truncate">{n.message}</p>
              )}
            </div>
            <button
              onClick={() => dismissNotification(n.id)}
              className="shrink-0 rounded p-0.5 text-slate-500 transition-colors hover:text-slate-300"
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
